'use client';

/**
 * EntityFilters Component
 * Filter bar for entity list with type, status, tags, and search
 */

import { useState, useEffect } from 'react';
import type { EntityType, EntityStatus } from '@/lib/command-center/types';
import { ENTITY_TYPE_LABELS, ENTITY_TYPE_CATEGORIES } from '@/lib/command-center/types';

export interface EntityFilterValues {
  search: string;
  types: EntityType[];
  status: EntityStatus | 'all';
  tags: string[];
}

interface EntityFiltersProps {
  filters: EntityFilterValues;
  onChange: (filters: EntityFilterValues) => void;
  availableTags?: string[];
}

// Status options
const STATUS_OPTIONS: { value: EntityStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'deprecated', label: 'Deprecated' },
];

export default function EntityFilters({ filters, onChange, availableTags = [] }: EntityFiltersProps) {
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.type-dropdown')) setShowTypeDropdown(false);
      if (!target.closest('.tag-dropdown')) setShowTagDropdown(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleType = (type: EntityType) => {
    const newTypes = filters.types.includes(type)
      ? filters.types.filter(t => t !== type)
      : [...filters.types, type];
    onChange({ ...filters, types: newTypes });
  };

  const toggleTag = (tag: string) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter(t => t !== tag)
      : [...filters.tags, tag];
    onChange({ ...filters, tags: newTags });
  };

  const clearFilters = () => {
    onChange({ search: '', types: [], status: 'all', tags: [] });
  };

  const hasActiveFilters = filters.search || filters.types.length > 0 || filters.status !== 'all' || filters.tags.length > 0;

  return (
    <div className="bg-surface/30 border border-border-muted rounded-lg p-4 mb-6">
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => onChange({ ...filters, search: e.target.value })}
              placeholder="Search entities..."
              className="w-full bg-background border border-border rounded-lg pl-10 pr-4 py-2 text-foreground text-sm placeholder-foreground-muted focus:border-accent focus:outline-none"
            />
          </div>
        </div>

        {/* Type Filter */}
        <div className="relative type-dropdown">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowTypeDropdown(!showTypeDropdown);
              setShowTagDropdown(false);
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${
              filters.types.length > 0
                ? 'bg-accent-muted border-border-muted0 text-accent'
                : 'bg-background border-border text-foreground-secondary hover:border-border/40'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span>Type {filters.types.length > 0 && `(${filters.types.length})`}</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showTypeDropdown && (
            <div className="absolute top-full left-0 mt-1 w-72 bg-background-dark border border-border rounded-lg shadow-xl z-20 max-h-80 overflow-y-auto">
              {Object.entries(ENTITY_TYPE_CATEGORIES).map(([category, types]) => (
                <div key={category} className="p-2">
                  <p className="text-foreground-tertiary text-xs font-semibold uppercase px-2 py-1">{category}</p>
                  {types.map(type => (
                    <label
                      key={type}
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-cream/5 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filters.types.includes(type)}
                        onChange={() => toggleType(type)}
                        className="rounded border-border/30 bg-background text-accent focus:ring-ring"
                      />
                      <span className="text-foreground text-sm">{ENTITY_TYPE_LABELS[type]}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Status Filter */}
        <select
          value={filters.status}
          onChange={(e) => onChange({ ...filters, status: e.target.value as EntityStatus | 'all' })}
          className={`px-4 py-2 rounded-lg border text-sm focus:outline-none ${
            filters.status !== 'all'
              ? 'bg-accent-muted border-border-muted0 text-accent'
              : 'bg-background border-border text-foreground-secondary'
          }`}
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {/* Tags Filter */}
        {availableTags.length > 0 && (
          <div className="relative tag-dropdown">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowTagDropdown(!showTagDropdown);
                setShowTypeDropdown(false);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${
                filters.tags.length > 0
                  ? 'bg-accent-muted border-border-muted0 text-accent'
                  : 'bg-background border-border text-foreground-secondary hover:border-border/40'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <span>Tags {filters.tags.length > 0 && `(${filters.tags.length})`}</span>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showTagDropdown && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-background-dark border border-border rounded-lg shadow-xl z-20 max-h-60 overflow-y-auto p-2">
                {availableTags.map(tag => (
                  <label
                    key={tag}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-cream/5 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={filters.tags.includes(tag)}
                      onChange={() => toggleTag(tag)}
                      className="rounded border-border/30 bg-background text-accent focus:ring-ring"
                    />
                    <span className="text-foreground text-sm">{tag}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-foreground-tertiary hover:text-foreground text-sm flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear
          </button>
        )}
      </div>

      {/* Active filter badges */}
      {(filters.types.length > 0 || filters.tags.length > 0) && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border-muted">
          {filters.types.map(type => (
            <span
              key={type}
              className="inline-flex items-center gap-1 px-2 py-1 bg-accent-muted text-accent text-xs rounded"
            >
              {ENTITY_TYPE_LABELS[type]}
              <button onClick={() => toggleType(type)} className="hover:text-accent">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
          {filters.tags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 bg-cream/10 text-foreground-secondary text-xs rounded"
            >
              #{tag}
              <button onClick={() => toggleTag(tag)} className="hover:text-foreground">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
